import { type LockResponse, SiteSystemType } from "api/ApiClient";
import { DEFAULT_CALL_IN_HOURS } from "components/AdminSites/dbDownloadCadence";

// ENGAGE "210" mode: the lock calls in over Wi-Fi on its own schedule.
const WIFI_OPERATING_MODE = "210";

// How late a call-in can be before it counts as missed. Generous: a lock already calls in up to the site's spread
// (default 30 min) after its slot, and a failed call-in retries later.
export const MISSED_CALL_IN_GRACE_MINUTES = 120;

export interface NextDbDownload {
  at: Date;
  // No time was sent, so this is the lock's own default counted from its last database download.
  estimated: boolean;
  overdue: boolean;
}

/** Same rule as Connect-API's SchlageConstants.IsSchlageWifiLock: Schlage site and mode exactly "210". */
export function isSchlageWifiLock(lock: LockResponse): boolean {
  return (
    lock.site?.account?.systemType === SiteSystemType.SchlageEngage &&
    lock.operatingMode === WIFI_OPERATING_MODE
  );
}

// The API sends these as UTC without an offset, which Date would read as local time.
function parseUtc(value: Date | string | undefined | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const parsed = new Date(hasOffset ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * When a Schlage Wi-Fi lock should next call in for its database: the time it was sent, or, when the site has no
 * cadence, an estimate from its last download and the locks' default. Null for every other lock.
 */
export function nextDbDownload(lock: LockResponse, now: Date = new Date()): NextDbDownload | null {
  if (!isSchlageWifiLock(lock)) return null;

  let at = parseUtc(lock.nextDatabaseDownloadTimestamp);
  let estimated = false;
  if (!at) {
    const lastDownload = parseUtc(lock.databaseSyncTimestamp);
    if (!lastDownload) return null;
    at = new Date(lastDownload.getTime() + DEFAULT_CALL_IN_HOURS * 60 * 60 * 1000);
    estimated = true;
  }
  const overdue = now.getTime() > at.getTime() + MISSED_CALL_IN_GRACE_MINUTES * 60 * 1000;
  return { at, estimated, overdue };
}
